import { createContext, useContext, useState, useEffect } from 'react';
import api from '../config/api';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';

const AuthContext = createContext();

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token') || null);
  const navigate = useNavigate();

  // Load user data on initial mount
  useEffect(() => {
    const loadUserFromStorage = async () => {
      setLoading(true);
      try {
        await checkAuthStatus();
      } catch (err) {
        console.error('Error loading user:', err);
      } finally {
        setLoading(false);
      }
    };

    loadUserFromStorage();
  }, []);

  
  // Set token in axios headers whenever it changes
  useEffect(() => {
    if (token) {
      api.defaults.headers.common['x-auth-token'] = token;
    } else {
      delete api.defaults.headers.common['x-auth-token'];
    }
  }, [token]);

  const checkAuthStatus = async () => {
    const storedToken = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');
    const storedUserType = localStorage.getItem('userType');
    const storedTokenExpiration = localStorage.getItem('tokenExpiration');
    
    if (!storedToken || !storedTokenExpiration) {
      setToken(null);
      setUser(null);
      return false;
    }
    
    const expirationDate = new Date(storedTokenExpiration);
    if (expirationDate < new Date()) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('userType');
      localStorage.removeItem('tokenExpiration');
      setToken(null);
      setUser(null);
      return false;
    }
    
    // Set token in state and axios headers
    setToken(storedToken);
    api.defaults.headers.common['x-auth-token'] = storedToken;
    
    // If we have stored user data, use it immediately to prevent UI flicker
    if (storedUser) {
      try {
        const userData = JSON.parse(storedUser);
        // Ensure the stored userType is set as the current role
        if (storedUserType && userData.roles?.includes(storedUserType)) {
          userData.role = storedUserType;
        }
        setUser(userData);
      } catch (e) {
        console.error('Error parsing stored user data:', e);
      }
    }
    
    try {
      // Verify token validity with backend - use profile endpoint instead of /me
      const response = await api.get('/auth/profile');
      const userData = response.data;
      
      // Ensure we maintain the current role from localStorage if it's valid
      if (storedUserType && userData.roles?.includes(storedUserType)) {
        userData.role = storedUserType;
      }
      
      // Update user data and store in localStorage
      setUser(userData);
      localStorage.setItem('user', JSON.stringify(userData));
      localStorage.setItem('userType', userData.role);
      
      return true;
    } catch (err) {
      console.error('Token validation error:', err);
      
      // Only clear if it's an authentication error
      if (err.response && (err.response.status === 401 || err.response.status === 403)) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem('userType');
        localStorage.removeItem('tokenExpiration');
        setToken(null);
        setUser(null);
        delete api.defaults.headers.common['x-auth-token'];
      }
      
      return false;
    }
  };

  const register = async (userData) => {
    try {
      setError(null);
      
      // Send registration request to backend
      const res = await api.post('/auth/register', userData);
      
      // Extract user data and token
      const { token, user } = res.data;
      
      // Ensure user role is properly set
      const userRole = user.role || 'donor';
      
      // Make sure user has both roles
      if (!user.roles || !Array.isArray(user.roles)) {
        user.roles = ['donor', 'requestor'];
      } else if (!user.roles.includes('donor')) {
        user.roles.push('donor');
      } else if (!user.roles.includes('requestor')) {
        user.roles.push('requestor');
      }
      
      // Save token and user data to local storage
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(user));
      localStorage.setItem('userType', userRole);
      
      // Set token in axios headers
      api.defaults.headers.common['x-auth-token'] = token;
      
      // Set token and user data in state
      setToken(token);
      setUser(user);
      
      // Redirect to appropriate dashboard based on role
      if (userRole === 'requestor') {
        navigate('/requester-dashboard');
      } else {
        navigate('/dashboard');
      }
      
      toast.success('Registration successful!');
      return res.data;
    } catch (err) {
      console.error('Registration error:', err);
      const errorMessage = err.response?.data?.message || 'Registration failed';
      setError(errorMessage);
      toast.error(errorMessage);
      throw err;
    }
  };

  const login = async (email, password, rememberMe = false, selectedRole = 'donor') => {
    try {
      setError(null);
      
      // Send login request to backend with proper data format
      const res = await api.post('/auth/login', { email, password });
      
      // Check if response is valid
      if (!res.data || !res.data.token || !res.data.user) {
        throw new Error('Invalid response from server');
      }
      
      // Extract user data and token
      const { token, user } = res.data;
      
      // Ensure user has roles array
      if (!user.roles || !Array.isArray(user.roles)) {
        user.roles = ['donor', 'requestor'];
      }
      
      // Check if user has the selected role, if not, allow login with default role
      if (!user.roles.includes(selectedRole)) {
        console.warn(`User doesn't have ${selectedRole} role, using default role: ${user.role || 'donor'}`);
        selectedRole = user.role || 'donor';
      }
      
      // Set the selected role as current role
      user.role = selectedRole;
      
      // Set expiration date based on rememberMe
      const expirationDate = new Date();
      if (rememberMe) {
        // Set for 30 days if rememberMe is true
        expirationDate.setDate(expirationDate.getDate() + 30);
      } else {
        // Set for 1 day if rememberMe is false
        expirationDate.setDate(expirationDate.getDate() + 1);
      }
      
      // Save token, user data, and expiration date to local storage
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(user));
      localStorage.setItem('userType', selectedRole);
      localStorage.setItem('tokenExpiration', expirationDate.toISOString());
      
      // Set token in axios headers
      api.defaults.headers.common['x-auth-token'] = token;
      
      // Set token and user data in state
      setToken(token);
      setUser(user);
      
      // Redirect to appropriate dashboard based on selected role
      if (selectedRole === 'requestor') {
        navigate('/requester-dashboard');
      } else {
        navigate('/dashboard');
      }
      
      toast.success(`Login successful! Welcome back as ${selectedRole === 'requestor' ? 'Requester' : 'Donor'}`);
      return res.data;
    } catch (err) {
      console.error('Login error:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Invalid credentials';
      setError(errorMessage);
      toast.error(errorMessage);
      throw err;
    }
  };

  const logout = async () => {
    try {
      // Clear local storage
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('userType');
      localStorage.removeItem('tokenExpiration');
      
      // Clear state
      setToken(null);
      setUser(null);
      
      // Clear auth header
      delete api.defaults.headers.common['x-auth-token'];
      
      // Redirect to home
      navigate('/');
      
      toast.success('Logged out successfully');
    } catch (err) {
      console.error('Logout error:', err);
      toast.error('Failed to logout');
    }
  };

  // Function to switch user role between donor and requestor
  const switchRole = async (newRole) => {
    try {
      setError(null);
      
      // Send role switch request to backend
      const res = await api.put('/auth/switch-role', { role: newRole });
      
      // Extract updated user data and token
      const { token, user } = res.data;
      
      // Save token and user data to local storage
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(user));
      localStorage.setItem('userType', user.role);
      
      // Set token in axios headers
      api.defaults.headers.common['x-auth-token'] = token;
      
      // Update state
      setToken(token);
      setUser(user);
      
      // Redirect to appropriate dashboard
      if (user.role === 'requestor') {
        navigate('/requester-dashboard');
      } else {
        navigate('/dashboard');
      }
      
      toast.success(`Switched to ${user.role} role`);
      return res.data;
    } catch (err) {
      console.error('Role switch error:', err);
      const errorMessage = err.response?.data?.message || 'Failed to switch role';
      setError(errorMessage);
      toast.error(errorMessage);
      throw err;
    }
  };

  const value = {
    user,
    token,
    loading,
    error,
    register,
    login,
    logout,
    switchRole,
    checkAuthStatus
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
